import { memo, useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react'
import { CheckIcon, TrashIcon, AddToCampaignIcon, InfoIcon } from '@/components/icons'
import CustomAssetIcon from '@/components/icons/CustomAssetIcon'
import LeadFormIcon from '@/components/icons/LeadFormIcon'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import CreativeThumbnail from '@/components/mediaBuying/adCampaign/screens/AdsSelection/CreativeThumbnail'
import { AudienceIcon } from '@/components/mediaBuying/adCampaign/AudienceIcon'
import useAdCreativesStore, {
    isCreativeEligibleForSelection,
    hasVisibleAssetVariations,
} from '@/store/adCreativesStore'
import { FUNNEL_STAGES } from '@/utils/ad-campaign-constants'
import { cn } from '@/lib/utils'
import { GET_CAMPAIGNS_WITH_ADSETS, ADD_CREATIVES_TO_CAMPAIGN } from '@/graphql/ads/ad-campaigns-queries'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { showNotification } from '@/utils/notifications'

const AddToCampaign = memo(() => {
    const navigate = useNavigate()
    const { id: campaignId } = useParams()
    const { connectedPublishers } = useMediaBuyingContext()

    // Use store selectors for better performance
    const getSelectedCreatives = useAdCreativesStore((state) => state.getSelectedCreatives)

    // Get selected creative objects using store selector
    const selectedCreativeObjects = getSelectedCreatives()

    // Get publisher ID
    const publisherId = connectedPublishers ? Object.keys(connectedPublishers)[0] : null

    // State for UI interactions
    const [expandedItems, setExpandedItems] = useState(new Set())
    const [selectedAdSets, setSelectedAdSets] = useState(new Set())
    const [removedCreatives, setRemovedCreatives] = useState(new Set())
    const [keepOriginalName, setKeepOriginalName] = useState(false)

    // Track row index for alternating colors
    const rowIndexRef = useRef(0)

    // Fetch campaigns with adsets
    const { data, loading, error } = useQuery(GET_CAMPAIGNS_WITH_ADSETS, {
        variables: {
            campaignId: campaignId,
        },
        fetchPolicy: 'network-only',
    })

    // Add creatives to campaign mutation
    const [addCreativesToCampaign, { loading: addingCreatives }] = useMutation(ADD_CREATIVES_TO_CAMPAIGN, {
        onCompleted: (data) => {
            if (data.addCreativesToCampaign.success) {
                showNotification(data.addCreativesToCampaign.message, 'success', 5000)
                // Redirect to ads manager
                navigate(`/campaigns/media-buying/${campaignId}/ads-manager`)
            }
        },
        onError: (error) => {
            console.error('Error adding creatives to campaign:', error)
            showNotification(error.message || 'Failed to add creatives to campaign', 'error', 5000)
        },
    })

    const campaigns = data?.AdCampaign?.campaignsWithAdSets || []

    // Toggle expansion
    const toggleExpanded = useCallback((itemId) => {
        setExpandedItems((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(itemId)) {
                newSet.delete(itemId)
            } else {
                newSet.add(itemId)
            }
            return newSet
        })
    }, [])

    // Get visible creatives (not removed)
    const visibleCreatives = selectedCreativeObjects.filter((creative) => !removedCreatives.has(creative.id))

    // Get the first visible creative for filtering rules (single selection mode)
    const selectedCreative = visibleCreatives[0] || null

    // Helper functions for filtering based on creative properties - MOVED HERE TO FIX HOISTING ISSUE
    const shouldDisableCampaign = useCallback(
        (campaign) => {
            if (!selectedCreative) return false

            const hasInstantForm = selectedCreative.lead_form_id !== null && selectedCreative.lead_form_id !== undefined
            // Rule 1: If creative has instant form, only allow campaigns with "leads" objective
            if (hasInstantForm && campaign.objective !== 'leads') {
                return true
            }

            return false
        },
        [selectedCreative],
    )

    const shouldDisableAdSet = useCallback(
        (adSet, campaign) => {
            // Rule 0: Ad sets without external_id are not published to Meta - cannot add creatives
            const hasExternalId = adSet.external_id && String(adSet.external_id).trim()
            if (!hasExternalId) return true

            if (!selectedCreative) return false

            const hasInstantForm = selectedCreative.lead_form_id !== null && selectedCreative.lead_form_id !== undefined

            // Rule 1: If creative has instant form, only allow ad sets that already have instant forms
            if (hasInstantForm) {
                if (campaign.objective !== 'leads') return true
                return !adSet.has_instant_forms
            }

            // Rule 2: If creative has no instant form, don't allow ad sets that already have instant forms
            if (!hasInstantForm && adSet.has_instant_forms) {
                return true
            }

            return false
        },
        [selectedCreative, shouldDisableCampaign],
    )

    // Handle checkbox selection with memoization
    const handleCheckboxChange = useCallback(
        (type, item, parentIds = {}) => {
            // Check if item should be disabled based on type
            if (type === 'campaign') {
                const campaign = campaigns.find((c) => c.id === item.id)
                if (!campaign || shouldDisableCampaign(campaign)) return

                // Get only enabled ad sets
                const enabledAdSetIds = campaign.adSetsByStage.flatMap((stage) =>
                    stage.adSets.filter((adSet) => !shouldDisableAdSet(adSet, campaign)).map((adSet) => adSet.db_id),
                )

                const allSelected = enabledAdSetIds.length > 0 && enabledAdSetIds.every((id) => selectedAdSets.has(id))

                setSelectedAdSets((prev) => {
                    const newSet = new Set(prev)
                    if (allSelected) {
                        // Deselect all enabled
                        enabledAdSetIds.forEach((id) => newSet.delete(id))
                    } else {
                        // Select all enabled
                        enabledAdSetIds.forEach((id) => newSet.add(id))
                    }
                    return newSet
                })
            } else if (type === 'stage') {
                // Toggle all enabled adsets in the stage
                const campaign = campaigns.find((c) => c.id === parentIds.campaignId)
                if (!campaign || shouldDisableCampaign(campaign)) return

                const stageData = campaign.adSetsByStage.find((s) => s.stage === item.stage)
                if (!stageData) return

                // Get only enabled ad sets in this stage
                const enabledAdSetIds = stageData.adSets
                    .filter((adSet) => !shouldDisableAdSet(adSet, campaign))
                    .map((adSet) => adSet.db_id)

                const allSelected = enabledAdSetIds.length > 0 && enabledAdSetIds.every((id) => selectedAdSets.has(id))

                setSelectedAdSets((prev) => {
                    const newSet = new Set(prev)
                    if (allSelected) {
                        // Deselect all enabled
                        enabledAdSetIds.forEach((id) => newSet.delete(id))
                    } else {
                        // Select all enabled
                        enabledAdSetIds.forEach((id) => newSet.add(id))
                    }
                    return newSet
                })
            } else if (type === 'adset') {
                // Check if this specific ad set should be disabled
                const campaign = campaigns.find((c) => c.id === parentIds.campaignId)
                if (!campaign || shouldDisableAdSet(item, campaign)) return

                // Toggle individual adset
                setSelectedAdSets((prev) => {
                    const newSet = new Set(prev)
                    if (newSet.has(item.db_id)) {
                        newSet.delete(item.db_id)
                    } else {
                        newSet.add(item.db_id)
                    }
                    return newSet
                })
            }
        },
        [campaigns, selectedAdSets, shouldDisableCampaign, shouldDisableAdSet],
    )

    // Check if checkbox should be selected with memoization
    const isSelected = useCallback(
        (type, item, parentIds = {}) => {
            if (type === 'campaign') {
                const campaign = campaigns.find((c) => c.id === item.id)
                if (!campaign) return false

                // Only consider enabled ad sets
                const enabledAdSetIds = campaign.adSetsByStage.flatMap((stage) =>
                    stage.adSets.filter((adSet) => !shouldDisableAdSet(adSet, campaign)).map((adSet) => adSet.db_id),
                )

                return enabledAdSetIds.length > 0 && enabledAdSetIds.every((id) => selectedAdSets.has(id))
            } else if (type === 'stage') {
                const campaign = campaigns.find((c) => c.id === parentIds.campaignId)
                if (!campaign) return false

                const stageData = campaign.adSetsByStage.find((s) => s.stage === item.stage)
                if (!stageData) return false

                // Only consider enabled ad sets in the stage
                const enabledAdSetIds = stageData.adSets
                    .filter((adSet) => !shouldDisableAdSet(adSet, campaign))
                    .map((adSet) => adSet.db_id)
                return enabledAdSetIds.length > 0 && enabledAdSetIds.every((id) => selectedAdSets.has(id))
            } else if (type === 'adset') {
                return selectedAdSets.has(item.db_id)
            }
            return false
        },
        [campaigns, selectedAdSets, shouldDisableAdSet],
    )

    // Check if checkbox is indeterminate with memoization
    const isIndeterminate = useCallback(
        (type, item, parentIds = {}) => {
            if (type === 'campaign') {
                const campaign = campaigns.find((c) => c.id === item.id)
                if (!campaign) return false

                // Only consider enabled ad sets
                const enabledAdSetIds = campaign.adSetsByStage.flatMap((stage) =>
                    stage.adSets.filter((adSet) => !shouldDisableAdSet(adSet, campaign)).map((adSet) => adSet.db_id),
                )

                const selectedCount = enabledAdSetIds.filter((id) => selectedAdSets.has(id)).length
                return selectedCount > 0 && selectedCount < enabledAdSetIds.length
            } else if (type === 'stage') {
                const campaign = campaigns.find((c) => c.id === parentIds.campaignId)
                if (!campaign) return false

                const stageData = campaign.adSetsByStage.find((s) => s.stage === item.stage)
                if (!stageData) return false

                // Only consider enabled ad sets in the stage
                const enabledAdSetIds = stageData.adSets
                    .filter((adSet) => !shouldDisableAdSet(adSet, campaign))
                    .map((adSet) => adSet.db_id)
                const selectedCount = enabledAdSetIds.filter((id) => selectedAdSets.has(id)).length
                return selectedCount > 0 && selectedCount < enabledAdSetIds.length
            }
            return false
        },
        [campaigns, selectedAdSets, shouldDisableAdSet],
    )

    // Remove creative from local view (without affecting store)
    const handleRemoveCreative = useCallback((creativeId) => {
        setRemovedCreatives((prev) => {
            const newSet = new Set(prev)
            newSet.add(creativeId)
            return newSet
        })
    }, [])

    // Redirect if no creatives are selected (page refresh or direct access)
    useEffect(() => {
        if (selectedCreativeObjects.length === 0) {
            navigate(`/campaigns/media-buying/${campaignId}/ad-library`)
        }
    }, [selectedCreativeObjects.length, navigate, campaignId])

    // Handle back navigation
    const handleBack = useCallback(() => {
        navigate(-1)
    }, [navigate])

    // Handle launch with optimized data preparation
    const handleLaunch = useCallback(async () => {
        if (!publisherId) {
            showNotification('Publisher not found. Please ensure you have connected publishers.', 'error', 5000)
            return
        }

        // Filter to only eligible creatives (Not Rejected, Not Processing, has asset variations)
        const eligibleCreatives = visibleCreatives.filter(
            (c) => isCreativeEligibleForSelection(c) && hasVisibleAssetVariations(c),
        )
        // Filter to only ad sets with external_id (published to Meta) - backend rejects unpublished ad sets
        const allAdSetsFlat = campaigns.flatMap((c) =>
            (c.adSetsByStage || []).flatMap((s) => (s.adSets || []).map((a) => ({ ...a, campaignId: c.id }))),
        )
        const publishedAdSetIds = allAdSetsFlat
            .filter((a) => a.external_id && String(a.external_id).trim())
            .map((a) => a.db_id)
        const selectedAdSetIds = Array.from(selectedAdSets).filter((id) => publishedAdSetIds.includes(id))
        const selectedCreativeIds = eligibleCreatives.map((c) => c.id)

        if (selectedAdSetIds.length === 0) {
            const hadSelection = selectedAdSets.size > 0
            showNotification(
                hadSelection
                    ? 'Selected ad sets are not published to Meta yet. Please publish them first or select different ad sets.'
                    : 'Please select at least one ad set',
                'error',
                5000,
            )
            return
        }

        if (eligibleCreatives.length === 0) {
            showNotification('Please select at least one eligible creative (Not Rejected)', 'error', 3000)
            return
        }

        try {
            await addCreativesToCampaign({
                variables: {
                    data: {
                        adSetIds: selectedAdSetIds,
                        creativeIds: selectedCreativeIds,
                        keepOriginalName: keepOriginalName,
                    },
                    publisherId: publisherId,
                },
            })
        } catch (error) {
            // Error is handled by onError callback
            console.error('Launch failed:', error)
        }
    }, [selectedAdSets, visibleCreatives, publisherId, addCreativesToCampaign, keepOriginalName])

    // Render checkbox with memoization
    const renderCheckbox = useCallback(
        (type, item, parentIds) => {
            const selected = isSelected(type, item, parentIds)
            const indeterminate = isIndeterminate(type, item, parentIds)

            // Determine if this checkbox should be disabled
            let isDisabled = false
            if (type === 'campaign') {
                const campaign = campaigns.find((c) => c.id === item.id)
                const campaignIsDisabled = campaign && shouldDisableCampaign(campaign)

                // Also check if all ad sets in the campaign are disabled
                const allAdSetsDisabled =
                    campaign?.adSetsByStage.every((stage) =>
                        stage.adSets.every((adSet) => shouldDisableAdSet(adSet, campaign)),
                    ) || false

                isDisabled = campaignIsDisabled || allAdSetsDisabled
            } else if (type === 'adset') {
                const campaign = campaigns.find((c) => c.id === parentIds.campaignId)
                isDisabled = campaign && shouldDisableAdSet(item, campaign)
            } else if (type === 'stage') {
                const campaign = campaigns.find((c) => c.id === parentIds.campaignId)
                // Stage is disabled if all its ad sets are disabled
                const stageData = campaign?.adSetsByStage.find((s) => s.stage === item.stage)
                isDisabled = stageData?.adSets.every((adSet) => shouldDisableAdSet(adSet, campaign)) || false
            }

            return (
                <div
                    className={cn(
                        'tw:w-6 tw:h-6 tw:rounded tw:border-2 tw:flex tw:items-center tw:justify-center',
                        isDisabled ? 'tw:cursor-not-allowed' : 'tw:cursor-pointer',
                        selected ? 'tw:bg-[#2450BF] tw:border-[#2450BF]' : 'tw:border-[#2450BF] tw:bg-transparent',
                        indeterminate && 'tw:bg-[#2450BF] tw:border-[#2450BF]',
                        isDisabled && 'tw:opacity-50 tw:border-[#4E6986]',
                        isDisabled && selected && 'tw:bg-[#4E6986] tw:border-[#4E6986]',
                    )}
                    onClick={isDisabled ? undefined : () => handleCheckboxChange(type, item, parentIds)}
                >
                    {selected && <CheckIcon className="tw:w-4.5 tw:h-4.5 tw:text-white" />}
                    {indeterminate && !selected && <div className="tw:w-3 tw:h-0.5 tw:bg-white" />}
                </div>
            )
        },
        [isSelected, isIndeterminate, handleCheckboxChange, campaigns, shouldDisableCampaign, shouldDisableAdSet],
    )

    // Render table row
    const renderRow = (type, item, level = 0, parentIds = {}) => {
        const itemId =
            type === 'campaign' ? item.id : type === 'stage' ? `${parentIds.campaignId}-${item.stage}` : item.db_id

        const hasChildren = type === 'campaign' || (type === 'stage' && item.adSets?.length > 0)
        const isExpanded = expandedItems.has(itemId)

        // Increment row index and determine row color
        rowIndexRef.current++
        const currentRowIndex = rowIndexRef.current
        const isDarkRow = currentRowIndex % 2 === 0 // 2, 4, 6, etc.
        const rowBgColor = isDarkRow ? 'tw:bg-[#071727]' : 'tw:bg-[#17273C]'
        const rowBorderColor = 'tw:border-[#2B3B4C]'

        // Determine active status
        const isActive =
            type === 'campaign' ? item.state === 'ACTIVE' : type === 'adset' ? item.status === 'ACTIVE' : true // stages are always active

        // Determine if this row should be disabled (for text color purposes - only affects ad sets)
        let isDisabled = false
        if (type === 'campaign') {
            // Campaign text color never changes, so we don't set isDisabled for campaigns
            isDisabled = false
        } else if (type === 'adset') {
            const campaign = campaigns.find((c) => c.id === parentIds.campaignId)
            isDisabled = campaign && shouldDisableAdSet(item, campaign)
        } else if (type === 'stage') {
            // Stage text color never changes, so we don't set isDisabled for stages
            isDisabled = false
        }

        // Get stage color for border
        const stageColor =
            type === 'stage'
                ? FUNNEL_STAGES[item.stage]?.color || '#D8CE41'
                : type === 'adset' && parentIds.stage
                  ? FUNNEL_STAGES[parentIds.stage]?.color || '#D8CE41'
                  : null

        return (
            <div key={itemId}>
                <div
                    className={cn(
                        'tw:flex tw:items-center tw:h-12 tw:border-b tw:transition-colors tw:border-l-4',
                        rowBgColor,
                        rowBorderColor,
                        'hover:tw:bg-[#0a1828]',
                    )}
                    style={{
                        borderLeftColor: type === 'campaign' ? (isDarkRow ? '#071727' : '#17273C') : stageColor,
                    }}
                >
                    <div className="tw:flex tw:items-center tw:flex-1 tw:px-6">
                        {/* Checkbox */}
                        <div className="tw:mr-3">{renderCheckbox(type, item, parentIds)}</div>

                        {/* Status indicator - only show for campaigns and adsets */}
                        {type !== 'stage' && (
                            <div
                                className={cn(
                                    'tw:w-3 tw:h-3 tw:rounded-full tw:mr-3',
                                    isActive ? 'tw:bg-green-500' : 'tw:bg-red-500',
                                )}
                            />
                        )}

                        {/* Expand/collapse and name */}
                        <div className="tw:flex tw:items-center tw:flex-1" style={{ paddingLeft: `${level * 24}px` }}>
                            {hasChildren && (
                                <button
                                    onClick={() => toggleExpanded(itemId)}
                                    className="tw:p-1 tw:hover:bg-[#243447] tw:rounded tw:mr-2"
                                >
                                    {isExpanded ? (
                                        <ChevronDownIcon size={16} className="tw:text-[#3871FF]" />
                                    ) : (
                                        <ChevronRightIcon size={16} className="tw:text-[#3871FF]" />
                                    )}
                                </button>
                            )}
                            {!hasChildren && <div className="tw:w-6 tw:mr-2" />}

                            {/* Name with stage color */}
                            <div className="tw:flex tw:items-center tw:gap-2">
                                {type === 'stage' && (
                                    <div
                                        className="tw:w-2 tw:h-2 tw:rounded-full"
                                        style={{ backgroundColor: FUNNEL_STAGES[item.stage]?.color || '#D8CE41' }}
                                    />
                                )}
                                {type === 'adset' && item.audience?.icon && (
                                    <AudienceIcon icon={item.audience.icon} size="sm" />
                                )}
                                <span className={cn('tw:text-sm', isDisabled ? 'tw:text-[#4E6986]' : 'tw:text-white')}>
                                    {type === 'campaign'
                                        ? item.name
                                        : type === 'stage'
                                          ? FUNNEL_STAGES[item.stage]?.name || item.stage
                                          : item.name}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Render children */}
                {hasChildren && isExpanded && (
                    <>
                        {type === 'campaign' &&
                            item.adSetsByStage.map((stageGroup) => (
                                <div key={`${item.id}-${stageGroup.stage}`}>
                                    {renderRow('stage', stageGroup, level + 1, { campaignId: item.id })}
                                    {expandedItems.has(`${item.id}-${stageGroup.stage}`) &&
                                        stageGroup.adSets.map((adSet) =>
                                            renderRow('adset', adSet, level + 2, {
                                                campaignId: item.id,
                                                stage: stageGroup.stage,
                                            }),
                                        )}
                                </div>
                            ))}
                    </>
                )}
            </div>
        )
    }

    return (
        <div className="tw:bg-[#071727] tw:h-full tw:text-white tw:flex tw:flex-col">
            {/* Header */}
            <div className="tw:flex tw:items-center tw:gap-3 tw:p-6 tw:border-b tw:border-[#2B3B4C]">
                <AddToCampaignIcon size={32} />
                <h1 className="tw:!text-xl tw:!font-medium tw:!m-0">Add To Campaign</h1>
            </div>

            {/* Main content */}
            <div className="tw:flex-1 tw:flex tw:overflow-hidden">
                {/* Left side - Campaign selection */}
                <div className="tw:flex-1 tw:flex tw:flex-col tw:border-r tw:border-[#2B3B4C]">
                    <div className="tw:p-6">
                        <h2 className="tw:!p-0 tw:!m-0 tw:!text-base tw:!font-medium">Select Campaign</h2>
                    </div>

                    <div className="tw:flex-1 tw:overflow-y-auto">
                        {loading && <div className="tw:p-6 tw:text-center tw:text-gray-400">Loading campaigns...</div>}

                        {error && (
                            <div className="tw:p-6 tw:text-center tw:text-red-400">
                                Error loading campaigns: {error.message}
                            </div>
                        )}

                        {!loading && !error && campaigns.length === 0 && (
                            <div className="tw:p-6 tw:text-center tw:text-gray-400">No campaigns found</div>
                        )}

                        {!loading &&
                            !error &&
                            (() => {
                                // Reset row index before rendering table
                                rowIndexRef.current = 0
                                return campaigns.map((campaign) => renderRow('campaign', campaign))
                            })()}
                    </div>
                </div>

                {/* Right side - Selected ads */}
                <div className="tw:w-[400px] tw:flex tw:flex-col tw:bg-[#0D1A2B]">
                    <div className="tw:p-6 tw:pb-4">
                        <h2 className="tw:!text-base tw:!font-medium tw:flex tw:items-center tw:gap-2">
                            Selected Ads
                            <span className="tw:bg-[#263E57] tw:text-white tw:rounded-full tw:w-6 tw:h-6 tw:text-xs tw:font-medium tw:flex tw:items-center tw:justify-center tw:flex-shrink-0">
                                {visibleCreatives.length}
                            </span>
                        </h2>
                    </div>

                    <div className="tw:flex-1 tw:overflow-y-auto">
                        <table className="tw:w-full">
                            <thead>
                                <tr className="tw:text-sm tw:text-gray-400 tw:border-b tw:border-[#2B3B4C]">
                                    <th className="tw:text-left tw:pb-3 tw:pl-6 tw:font-normal">Ad</th>
                                    <th className="tw:!text-right tw:pr-6 tw:pb-3 tw:font-normal">Placements</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleCreatives.map((creative) => (
                                    <tr
                                        key={creative.id}
                                        className="tw:!border-t tw:!border-b tw:!border-[#2B3B4C] tw:group tw:hover:bg-[#122243] tw:transition-colors"
                                    >
                                        <td className="tw:py-3 tw:pl-6">
                                            <div className="tw:flex tw:items-center tw:gap-3">
                                                <CreativeThumbnail
                                                    creative={{ id: creative.id }}
                                                    size={40}
                                                    showDeleteIcon={false}
                                                />
                                                <span className="tw:text-sm">
                                                    {creative.name || 'Untitled Creative'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="tw:py-3 tw:pr-6 tw:relative">
                                            <div className="tw:flex tw:items-center tw:justify-end tw:gap-2">
                                                <div className="tw:flex tw:items-center tw:gap-1">
                                                    {creative.lead_form_id && (
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <div className="tw:flex tw:items-center">
                                                                    <LeadFormIcon />
                                                                </div>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <span>Lead form is attached to this creative</span>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                                <span className="tw:text-sm">{selectedAdSets.size}</span>
                                            </div>
                                            {visibleCreatives.length > 1 && (
                                                <div
                                                    className="tw:absolute tw:top-0 tw:right-0 tw:h-full tw:group-hover:flex tw:hidden tw:items-center tw:w-[100px]"
                                                    style={{
                                                        background:
                                                            'linear-gradient(90deg, rgba(7, 23, 39, 0) 0.78%, #071727 40.47%)',
                                                    }}
                                                >
                                                    <button
                                                        onClick={() => handleRemoveCreative(creative.id)}
                                                        className="tw:absolute tw:right-6 tw:py-2"
                                                        type="button"
                                                    >
                                                        <TrashIcon className="tw:w-5 tw:h-5 tw:cursor-pointer tw:text-blue-400 hover:tw:text-blue-300" />
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {visibleCreatives.length === 0 && (
                            <div className="tw:text-center tw:text-gray-400 tw:mt-8">No ads selected</div>
                        )}

                        {/* Warning messages based on creative properties */}
                        {selectedCreative && (
                            <>
                                {/* Lead Forms Warning - with forms */}
                                {selectedCreative.lead_form_id !== null &&
                                selectedCreative.lead_form_id !== undefined ? (
                                    <div className="tw:p-6 tw:border-t tw:border-[#2B3B4C]">
                                        <div className="tw:flex tw:items-start tw:gap-3">
                                            <InfoIcon size={24} className="tw:text-[#FFD324] tw:flex-shrink-0" />
                                            <div>
                                                <h3 className="tw:!text-sm tw:!font-semibold tw:mb-2">
                                                    Lead Forms Limitation
                                                </h3>
                                                <p className="tw:text-sm tw:leading-relaxed">
                                                    To avoid a conflict, you cannot use the same audience for ads with
                                                    lead forms and ads without them. Because you have selected creatives
                                                    with lead forms, any audiences already in use by other ad types are
                                                    disabled.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    /* Lead Forms Warning - without forms */
                                    <div className="tw:p-6 tw:border-t tw:border-[#2B3B4C]">
                                        <div className="tw:flex tw:items-start tw:gap-3">
                                            <InfoIcon size={24} className="tw:text-[#FFD324] tw:flex-shrink-0" />
                                            <div>
                                                <h3 className="tw:!text-sm tw:!font-semibold tw:mb-2">
                                                    Lead Forms Limitation
                                                </h3>
                                                <p className="tw:text-sm tw:leading-relaxed">
                                                    To avoid a conflict, you cannot use the same audience for ads with
                                                    lead forms and ads without them. Because you have selected creatives
                                                    without lead forms, any audiences already in use by other ad types
                                                    are disabled.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="tw:border-t tw:border-[#2B3B4C]">
                {/* Checkbox option */}
                <div className="tw:p-6 tw:pb-4">
                    <div className="tw:flex tw:items-center tw:gap-2">
                        <div
                            className={cn(
                                'tw:w-6 tw:h-6 tw:rounded tw:border-2 tw:flex tw:items-center tw:justify-center tw:cursor-pointer',
                                keepOriginalName
                                    ? 'tw:bg-[#2450BF] tw:border-[#2450BF]'
                                    : 'tw:border-[#2450BF] tw:bg-transparent',
                            )}
                            onClick={() => setKeepOriginalName(!keepOriginalName)}
                        >
                            {keepOriginalName && <CheckIcon className="tw:w-4.5 tw:h-4.5 tw:text-white" />}
                        </div>
                        <span className="tw:text-sm tw:text-white">Keep the original ad name for duplicated ads</span>
                        <Tooltip>
                            <TooltipTrigger>
                                <InfoIcon className="tw:w-5 tw:h-5 tw:text-gray-400" />
                            </TooltipTrigger>
                            <TooltipContent showArrow={false}>
                                <div className="tw:max-w-sm">
                                    When enabled, duplicate ads will keep the original creative name. When disabled,
                                    duplicate ads will have "Copy" and timestamp added to distinguish them.
                                </div>
                            </TooltipContent>
                        </Tooltip>
                    </div>
                </div>

                {/* Separator */}
                <div className="tw:border-t tw:border-[#2B3B4C] tw:mb-4"></div>

                {/* Buttons */}
                <div className="tw:flex tw:justify-end tw:gap-4 tw:p-6 tw:pt-0">
                    <Button variant="secondary" className="tw:w-[156px]" onClick={handleBack}>
                        Back
                    </Button>
                    <Button
                        variant="default"
                        className="tw:w-[156px]"
                        onClick={handleLaunch}
                        disabled={selectedAdSets.size === 0 || visibleCreatives.length === 0 || addingCreatives}
                    >
                        {addingCreatives ? 'Publishing...' : 'Launch'}
                    </Button>
                </div>
            </div>
        </div>
    )
})

export default AddToCampaign
